/**
 * member-grants-core.js — pure core for the org Members page "Access ·
 * Domains & certificates" panel (owner decision 2026-09-24).
 *
 * An org admin gives a member who is NOT an org admin access to the org's
 * domains, DNS records and certificates — for the whole organisation, or for
 * single domains, with checkboxes.
 *
 * Contract mirrored (auth-api org-admin surface, cs-packages v2.6 scope_param):
 *   GET  /auth/orgs/{org}/members/{user}/capabilities?resources=domain,dns_record,certificate
 *        → { domain: {view:[scope…], manage:[…]}, dns_record:{…}, certificate:{…} }
 *          Each scope entry is `"org:3"` / `"org:3/domain:msda.ge"`, or the
 *          object form `{scope_path, permission_id}` — the object form is the
 *          one that can be revoked (see NEEDS_GRANT_ID below).
 *   POST /auth/orgs/{org}/grants
 *        { user_id, resource, action:'read', scope_path, can_read:true,
 *          field_permissions: { '<cap>': { enabled: true } } }
 *   POST /auth/orgs/{org}/grants/{permission_id}/revoke   { reason }
 *
 * `certificate.issue` is the EXISTING certificate-issue grant — the row shape
 * here is the same one `hasCertificateIssueGrant()` in certs-issue-core.js
 * reads back (`field_permissions.issue.enabled`). It is not forked.
 *
 * No `@/` imports — keep this module pure so `node --test` exercises it.
 */
import { isUploadOnly } from './domains-core.js'

const str = (v) => (v === null || v === undefined ? '' : String(v))

// ── the matrix ──────────────────────────────────────────────────────────────

/** `manage` / `edit` / `issue` each include `view` — the implication is shown, not only written. */
export const IMPLIES = { manage: 'view', edit: 'view', issue: 'view' }

export const IMPLIED_NOTE = 'Manage, Edit and Issue each include View, so View is ticked for you.'

/**
 * The three resources and their capabilities, in the order the grid renders.
 * `write` is the capability that implies `view`.
 */
export const CAPABILITY_MATRIX = [
  {
    resource: 'domain',
    label: 'Domains',
    chip: 'Domains',
    hint: 'The domains this organisation owns.',
    caps: [
      { cap: 'view', label: 'View', hint: 'See the domain and its status.' },
      { cap: 'manage', label: 'Manage', hint: 'Claim, verify and release domains.' }
    ]
  },
  {
    resource: 'dns_record',
    label: 'DNS records',
    chip: 'DNS',
    hint: 'The records inside those domains.',
    caps: [
      { cap: 'view', label: 'View', hint: 'Read the record list.' },
      { cap: 'edit', label: 'Edit', hint: 'Add, change and delete records.' }
    ]
  },
  {
    resource: 'certificate',
    label: 'Certificates',
    chip: 'Certs',
    hint: 'TLS certificates for those names.',
    caps: [
      { cap: 'view', label: 'View', hint: 'See certificates and their expiry.' },
      { cap: 'issue', label: 'Issue', hint: 'Request a new certificate or upload one.' }
    ]
  }
]

export const RESOURCES = CAPABILITY_MATRIX.map((r) => r.resource)

/** Every capability of a resource, or [] for an unknown resource. */
export function capsOf(resource) {
  const row = CAPABILITY_MATRIX.find((r) => r.resource === resource)
  return row ? row.caps.map((c) => c.cap) : []
}

/** The write capability of a resource (`manage` / `edit` / `issue`), or null. */
export function writeCapOf(resource) {
  return capsOf(resource).find((c) => c !== 'view') || null
}

/** Is `cap` a real capability of `resource`? */
export function isKnownCap(resource, cap) {
  return capsOf(resource).includes(cap)
}

// ── scope paths ─────────────────────────────────────────────────────────────

export const SCOPE_ORG = 'org'
export const SCOPE_DOMAINS = 'domains'

/** `org:3` — the whole organisation. */
export function orgScope(orgId) {
  const id = str(orgId).trim()
  return id ? `org:${id}` : ''
}

/** `org:3/domain:msda.ge` — one domain inside the organisation. */
export function domainScope(orgId, zone) {
  const root = orgScope(orgId)
  const z = str(zone).trim().toLowerCase()
  return root && z ? `${root}/domain:${z}` : ''
}

/**
 * `org:3/domain:msda.ge` → `{ orgId: '3', zone: 'msda.ge' }`;
 * `org:3` → `{ orgId: '3', zone: null }`; anything else → null.
 */
export function parseScopePath(path) {
  const segs = str(path).split('/').map((s) => s.trim()).filter(Boolean)
  if (!segs.length || !segs[0].startsWith('org:')) return null
  const orgId = segs[0].slice(4).trim()
  if (!orgId) return null
  if (segs.length === 1) return { orgId, zone: null }
  if (segs.length === 2 && segs[1].startsWith('domain:')) {
    const zone = segs[1].slice(7).trim().toLowerCase()
    return zone ? { orgId, zone } : null
  }
  return null
}

/** Is this scope path inside `orgId`? */
export function scopeBelongsToOrg(path, orgId) {
  const p = parseScopePath(path)
  return !!p && p.orgId === str(orgId).trim()
}

/** Customer-readable scope: "whole org" or the zone itself. */
export function scopeLabel(path) {
  const p = parseScopePath(path)
  if (!p) return str(path) || 'unknown scope'
  return p.zone || 'whole org'
}

// ── domains ─────────────────────────────────────────────────────────────────

/** The zone a Domain object names — `zone` wins, `domain` is the fallback. */
export function zoneOf(domain) {
  if (!domain) return ''
  if (typeof domain === 'string') return domain.trim().toLowerCase()
  return str(domain.zone || domain.domain).trim().toLowerCase()
}

/** Normalise a Domain[] into `{ zone, uploadOnly }[]`, deduped and sorted. */
export function domainOptions(domains) {
  const seen = new Map()
  for (const d of Array.isArray(domains) ? domains : []) {
    const zone = zoneOf(d)
    if (!zone || seen.has(zone)) continue
    seen.set(zone, { zone, uploadOnly: isUploadOnly(typeof d === 'string' ? null : d) })
  }
  return [...seen.values()].sort((a, b) => a.zone.localeCompare(b.zone))
}

/** The zones that may only take an uploaded certificate (A-26 `byo-only`). */
export function uploadOnlyZones(domains) {
  return domainOptions(domains).filter((d) => d.uploadOnly).map((d) => d.zone)
}

/** Filter a domain list by a free-text query; an empty query keeps everything. */
export function filterDomains(options, query) {
  const q = str(query).trim().toLowerCase()
  if (!q) return options
  return options.filter((d) => d.zone.includes(q))
}

/** The domain picker gets a search box once the list stops being scannable. */
export const SEARCH_THRESHOLD = 8

export function needsSearch(options) {
  return (Array.isArray(options) ? options.length : 0) > SEARCH_THRESHOLD
}

// ── the selection (what the panel's controls hold) ───────────────────────────

/** An untouched panel: whole organisation, nothing ticked. */
export function emptySelection() {
  return {
    scope: SCOPE_ORG,
    zones: [],
    caps: {
      domain: { view: false, manage: false },
      dns_record: { view: false, edit: false },
      certificate: { view: false, issue: false }
    }
  }
}

function cloneSelection(selection) {
  const base = emptySelection()
  if (!selection || typeof selection !== 'object') return base
  const out = {
    scope: selection.scope === SCOPE_DOMAINS ? SCOPE_DOMAINS : SCOPE_ORG,
    zones: Array.isArray(selection.zones)
      ? [...new Set(selection.zones.map((z) => str(z).trim().toLowerCase()).filter(Boolean))].sort()
      : [],
    caps: base.caps
  }
  for (const resource of RESOURCES) {
    const src = (selection.caps && selection.caps[resource]) || {}
    for (const cap of capsOf(resource)) out.caps[resource][cap] = !!src[cap]
  }
  return out
}

/**
 * Force `view` on wherever a write capability is on. Returns a NEW selection —
 * the panel calls it on every tick so the implication is visible in the grid.
 */
export function applyImplied(selection) {
  const next = cloneSelection(selection)
  for (const resource of RESOURCES) {
    const write = writeCapOf(resource)
    if (write && next.caps[resource][write]) next.caps[resource].view = true
  }
  return next
}

/**
 * What the panel's controls must hold to be truthful: implied `view` ticked,
 * and `certificate.issue` cleared when the scope is entirely upload-only (a
 * member may already hold such a grant — the box must not show a tick the
 * panel refuses to let you set).
 */
export function normalizeSelection(selection, domains = []) {
  const next = applyImplied(selection)
  if (issueGate(next, domains).disabled) {
    next.caps.certificate.issue = false
    return applyImplied(next)
  }
  return next
}

/** True when this `view` box is only ticked because a write capability implies it. */
export function isViewImplied(selection, resource) {
  const write = writeCapOf(resource)
  return !!(write && selection && selection.caps && selection.caps[resource] && selection.caps[resource][write])
}

/** Nothing ticked at all — Save has nothing to write and the row has no chips. */
export function isSelectionEmpty(selection) {
  const s = cloneSelection(selection)
  return RESOURCES.every((r) => capsOf(r).every((c) => !s.caps[r][c]))
}

// ── byo-only (A-26): Issue is never granted under an upload-only domain ──────

export const UPLOAD_ONLY_ISSUE_NOTE =
  'Your organisation supplies the certificate for these domains, so nobody can be granted Issue on them. Grant View instead — uploading is covered by the environment’s certificate tab.'

/**
 * Whether `certificate.issue` may be ticked, and what it will skip.
 *
 *   → { disabled, blocked:[zone…], allowed:[zone…], note }
 *
 * `disabled` only when EVERY domain in scope is upload-only. With a mix, the
 * box stays tickable and `blocked` names the domains it will not cover.
 */
export function issueGate(selection, domains) {
  const s = cloneSelection(selection)
  const options = domainOptions(domains)
  const inScope = s.scope === SCOPE_DOMAINS
    ? options.filter((d) => s.zones.includes(d.zone))
    : options
  const blocked = inScope.filter((d) => d.uploadOnly).map((d) => d.zone)
  const allowed = inScope.filter((d) => !d.uploadOnly).map((d) => d.zone)

  if (s.scope === SCOPE_DOMAINS && inScope.length > 0 && allowed.length === 0) {
    return { disabled: true, blocked, allowed, note: UPLOAD_ONLY_ISSUE_NOTE }
  }
  if (blocked.length) {
    const names = blocked.join(', ')
    return {
      disabled: false,
      blocked,
      allowed,
      note: `Upload only: ${names}. Issue does not apply there — your organisation supplies those certificates.`
    }
  }
  return { disabled: false, blocked: [], allowed, note: null }
}

// ── desired state ───────────────────────────────────────────────────────────

/** A stable key for one capability at one scope. */
export function entryKey(entry) {
  return `${entry.resource}:${entry.cap}@${entry.scopePath}`
}

function sortEntries(entries) {
  return [...entries].sort((a, b) => entryKey(a).localeCompare(entryKey(b)))
}

/**
 * The grant rows the ticked boxes ask for: one per (resource, capability,
 * scope). `certificate.issue` is dropped at any upload-only domain scope
 * (A-26) — including whole-organisation scope, where it is expanded to the
 * allowed domains rather than written across the org.
 */
export function selectionToDesired(selection, orgId, domains = []) {
  const s = applyImplied(selection)
  const root = orgScope(orgId)
  if (!root) return []
  const options = domainOptions(domains)
  const upload = new Set(options.filter((d) => d.uploadOnly).map((d) => d.zone))

  const scopesFor = (resource, cap) => {
    if (s.scope === SCOPE_DOMAINS) {
      const zones = s.zones.filter((z) => !(resource === 'certificate' && cap === 'issue' && upload.has(z)))
      return zones.map((z) => domainScope(orgId, z))
    }
    // Whole organisation. Issue cannot ride over an upload-only domain, so
    // when the org owns one we name the allowed domains explicitly instead.
    if (resource === 'certificate' && cap === 'issue' && upload.size > 0) {
      return options.filter((d) => !d.uploadOnly).map((d) => domainScope(orgId, d.zone))
    }
    return [root]
  }

  const out = []
  for (const resource of RESOURCES) {
    for (const cap of capsOf(resource)) {
      if (!s.caps[resource][cap]) continue
      for (const scopePath of scopesFor(resource, cap)) {
        if (scopePath) out.push({ resource, cap, scopePath })
      }
    }
  }
  const seen = new Set()
  return sortEntries(out.filter((e) => {
    const k = entryKey(e)
    if (seen.has(k)) return false
    seen.add(k)
    return true
  }))
}

// ── current state (what the capabilities read returns) ───────────────────────

/** A scope entry is a bare path or `{scope_path, permission_id}`. */
function readScopeEntry(raw) {
  if (typeof raw === 'string') return { scopePath: raw.trim(), permissionId: null }
  if (raw && typeof raw === 'object') {
    const scopePath = str(raw.scope_path || raw.scopePath || raw.scope).trim()
    const id = raw.permission_id ?? raw.permissionId ?? raw.id ?? null
    return { scopePath, permissionId: id === null || id === undefined || id === '' ? null : id }
  }
  return { scopePath: '', permissionId: null }
}

/**
 * `{ domain: { view: ['org:3'], manage: [{scope_path, permission_id}] } }`
 * → `[{ resource, cap, scopePath, permissionId }]`. Unknown resources and
 * capabilities, and scopes outside the org, are dropped — fail closed.
 */
export function capabilitiesToCurrent(payload, orgId) {
  const body = payload && typeof payload === 'object' && !Array.isArray(payload)
    ? (payload.capabilities && typeof payload.capabilities === 'object' ? payload.capabilities : payload)
    : null
  if (!body) return []
  const out = []
  for (const resource of RESOURCES) {
    const caps = body[resource]
    if (!caps || typeof caps !== 'object') continue
    for (const cap of Object.keys(caps)) {
      if (!isKnownCap(resource, cap)) continue
      const list = Array.isArray(caps[cap]) ? caps[cap] : []
      for (const raw of list) {
        const { scopePath, permissionId } = readScopeEntry(raw)
        if (!scopePath || !scopeBelongsToOrg(scopePath, orgId)) continue
        out.push({ resource, cap, scopePath, permissionId })
      }
    }
  }
  const seen = new Set()
  return sortEntries(out.filter((e) => {
    const k = entryKey(e)
    if (seen.has(k)) return false
    seen.add(k)
    return true
  }))
}

/**
 * Best-fit the current rows back into the panel's controls.
 *
 *   → { selection, mixed }
 *
 * `mixed` is true when the rows cannot be expressed by one scope choice — a
 * member holding `domain.view` on the whole org and `domain.manage` on one
 * domain, say. The panel says so out loud rather than flattening in silence.
 *
 * `_orgId` is accepted (and ignored) so callers read symmetrically with
 * `capabilitiesToCurrent(payload, orgId)`, which is where the org filter runs.
 */
export function currentToSelection(current, _orgId) {
  const entries = Array.isArray(current) ? current : []
  const selection = emptySelection()
  if (!entries.length) return { selection, mixed: false }

  const zones = new Set()
  let sawOrg = false
  for (const e of entries) {
    if (!isKnownCap(e.resource, e.cap)) continue
    selection.caps[e.resource][e.cap] = true
    const parsed = parseScopePath(e.scopePath)
    if (!parsed) continue
    if (parsed.zone) zones.add(parsed.zone); else sawOrg = true
  }

  if (sawOrg && zones.size === 0) {
    selection.scope = SCOPE_ORG
    return { selection, mixed: false }
  }
  selection.scope = SCOPE_DOMAINS
  selection.zones = [...zones].sort()
  // Mixed when org-wide and per-domain rows live side by side, or when the
  // per-domain rows do not all carry the same capability set.
  let mixed = sawOrg && zones.size > 0
  if (!mixed && zones.size > 1) {
    const perZone = new Map()
    for (const e of entries) {
      const parsed = parseScopePath(e.scopePath)
      if (!parsed || !parsed.zone) continue
      if (!perZone.has(parsed.zone)) perZone.set(parsed.zone, new Set())
      perZone.get(parsed.zone).add(`${e.resource}:${e.cap}`)
    }
    const shapes = [...perZone.values()].map((s) => [...s].sort().join('|'))
    mixed = new Set(shapes).size > 1
  }
  return { selection, mixed }
}

export const MIXED_NOTE =
  'This member’s access was set in more than one shape (some whole-organisation, some per-domain). Saving replaces all of it with what you pick here.'

// ── the diff ────────────────────────────────────────────────────────────────

/**
 * What Save must do: grant everything desired that is missing, revoke
 * everything current that is no longer wanted. Nothing else is touched.
 *
 *   → { grants: [{resource, cap, scopePath}], revokes: [{…, permissionId}] }
 */
export function diffGrants(current, desired) {
  const cur = Array.isArray(current) ? current : []
  const want = Array.isArray(desired) ? desired : []
  const curKeys = new Map(cur.map((e) => [entryKey(e), e]))
  const wantKeys = new Set(want.map(entryKey))

  const grants = sortEntries(want.filter((e) => !curKeys.has(entryKey(e))))
  const revokes = sortEntries(cur.filter((e) => !wantKeys.has(entryKey(e))))
  return { grants, revokes }
}

/** True when Save has nothing to do. */
export function isNoop(plan) {
  return !!plan && plan.grants.length === 0 && plan.revokes.length === 0
}

/** "2 to add, 1 to remove" — the count the footer carries. */
export function describePlan(plan) {
  if (!plan || isNoop(plan)) return 'No changes to save.'
  const parts = []
  if (plan.grants.length) parts.push(`${plan.grants.length} to add`)
  if (plan.revokes.length) parts.push(`${plan.revokes.length} to remove`)
  return parts.join(', ')
}

/** The sentence above the spelled-out plan. */
export function planCaption(plan) {
  if (!plan || isNoop(plan)) return 'No changes to save.'
  const a = plan.grants.length
  const r = plan.revokes.length
  const thing = (n) => `${n} ${n === 1 ? 'permission' : 'permissions'}`
  if (a && r) return `Save will add ${thing(a)} and remove ${thing(r)}:`
  if (a) return `Save will add ${thing(a)}:`
  return `Save will remove ${thing(r)}:`
}

/** How many changes the panel names one by one before it hides the rest. */
export const PLAN_PREVIEW_LIMIT = 6

/**
 * The plan, spelled out, so nobody saves a permissions change blind — some
 * revokes are implied rather than unticked (an existing `certificate.issue`
 * on an upload-only domain, say) and would otherwise be invisible.
 *
 *   → { rows: [{action, label, key}], more: <n> }
 */
export function planEntries(plan, limit = PLAN_PREVIEW_LIMIT) {
  if (!plan) return { rows: [], more: 0 }
  const all = [
    ...plan.grants.map((entry) => ({ action: 'add', entry })),
    ...plan.revokes.map((entry) => ({ action: 'remove', entry }))
  ]
  const rows = all.slice(0, limit).map((r) => ({
    action: r.action,
    label: entryLabel(r.entry),
    key: `${r.action}:${entryKey(r.entry)}`
  }))
  return { rows, more: Math.max(0, all.length - rows.length) }
}

// ── request bodies ──────────────────────────────────────────────────────────

/**
 * The grant row: an explicit read grant carrying the capability in
 * `field_permissions`. `action: 'read'` is what the existing org-admin
 * endpoint translates into `can_read`; `can_read` is sent too so a backend
 * reading the flag directly sees the same thing.
 */
export function grantBody(userId, entry, justification) {
  return {
    user_id: typeof userId === 'number' ? userId : Number(userId),
    resource: entry.resource,
    action: 'read',
    can_read: true,
    scope_path: entry.scopePath,
    field_permissions: { [entry.cap]: { enabled: true } },
    justification: justification || `Members page: ${entry.resource}.${entry.cap} on ${scopeLabel(entry.scopePath)}`
  }
}

export function revokeBody(entry) {
  return { reason: `Members page: removed ${entry.resource}.${entry.cap} on ${scopeLabel(entry.scopePath)}` }
}

/** Customer-readable name of one change, for the per-change result list. */
export function entryLabel(entry) {
  const row = CAPABILITY_MATRIX.find((r) => r.resource === entry.resource)
  const cap = row && row.caps.find((c) => c.cap === entry.cap)
  return `${row ? row.label : entry.resource} · ${cap ? cap.label : entry.cap} · ${scopeLabel(entry.scopePath)}`
}

// ── summary chips on the member row ─────────────────────────────────────────

/**
 * One chip per resource the member holds anything on, naming the strongest
 * capability and where it applies — "Domains: manage · msda.ge".
 */
export function summaryChips(current) {
  const entries = Array.isArray(current) ? current : []
  const chips = []
  for (const row of CAPABILITY_MATRIX) {
    const mine = entries.filter((e) => e.resource === row.resource)
    if (!mine.length) continue
    const write = writeCapOf(row.resource)
    const cap = write && mine.some((e) => e.cap === write) ? write : 'view'
    const scoped = mine.filter((e) => e.cap === cap)
    const zones = [...new Set(scoped.map((e) => parseScopePath(e.scopePath)).filter(Boolean).map((p) => p.zone).filter(Boolean))].sort()
    const wholeOrg = scoped.some((e) => {
      const p = parseScopePath(e.scopePath)
      return p && !p.zone
    })
    let where
    if (wholeOrg) where = 'whole org'
    else if (zones.length === 0) where = 'whole org'
    else if (zones.length <= 2) where = zones.join(', ')
    else where = `${zones.slice(0, 2).join(', ')} +${zones.length - 2}`
    chips.push({
      key: `${row.resource}:${cap}`,
      resource: row.resource,
      label: `${row.chip}: ${cap} · ${where}`,
      strong: cap !== 'view'
    })
  }
  return chips
}

export const NO_ACCESS_CHIP = 'No domain or certificate access'

// ── honest states ───────────────────────────────────────────────────────────

export const NOT_AVAILABLE_NOTE =
  'Granting domain and certificate access is not available in this environment yet.'

/** A 404 (or a 501) on the capabilities read means the backend half is not deployed. */
export function isNotAvailable(err) {
  const status = err && err.response && err.response.status
  return status === 404 || status === 501
}

/** A 403 is a real refusal — the caller is not an org admin here. */
export function isForbidden(err) {
  return !!(err && err.response && err.response.status === 403)
}

/** Best message out of an axios error, never the raw stack. */
export function errorMessage(err, fallback = 'Request failed') {
  const body = (err && err.response && err.response.data) || null
  if (body && typeof body === 'object') {
    if (typeof body.message === 'string' && body.message) return body.message
    if (Array.isArray(body.errors) && body.errors.length) {
      const first = body.errors[0]
      if (typeof first === 'string') return first
      if (first && (first.message || first.error_code)) return str(first.message || first.error_code)
    }
  }
  return (err && err.message) || fallback
}

export const MISSING_ID_NOTE =
  'The backend did not return a grant id for this row, so it could not be removed. Ask your operator to revoke it.'

// ── the client ──────────────────────────────────────────────────────────────

/**
 * A thin client over an axios-like `http` (already carrying the auth header
 * and the `/api` base). Kept here so the tests drive it with a fake.
 */
export function createMemberGrantsApi(http) {
  return {
    /** The member's current capabilities. Throws; the caller maps 404 → "not available yet". */
    async capabilities(orgId, userId) {
      const response = await http.get(
        `/auth/orgs/${orgId}/members/${userId}/capabilities`,
        { params: { resources: RESOURCES.join(',') } }
      )
      const data = response && response.data ? (response.data.data ?? response.data) : null
      return capabilitiesToCurrent(data, orgId)
    },

    /** Create one grant row. Returns the new permission id when the backend names it. */
    async grant(orgId, userId, entry) {
      const response = await http.post(`/auth/orgs/${orgId}/grants`, grantBody(userId, entry))
      const data = response && response.data ? (response.data.data ?? response.data) : null
      return data && (data.permission_id ?? data.id) !== undefined ? (data.permission_id ?? data.id) : null
    },

    /** Revoke one grant row by its permission id. */
    async revoke(orgId, entry) {
      await http.post(`/auth/orgs/${orgId}/grants/${entry.permissionId}/revoke`, revokeBody(entry))
      return true
    }
  }
}

/**
 * Run a plan, one change at a time, and report every one of them. A failure
 * never stops the rest and is never swallowed: the caller renders `failed`.
 *
 *   → { applied: [{entry, action}], failed: [{entry, action, message}] }
 */
export async function applyPlan(api, { orgId, userId, plan }) {
  const applied = []
  const failed = []

  for (const entry of plan.grants) {
    try {
      await api.grant(orgId, userId, entry)
      applied.push({ entry, action: 'grant' })
    } catch (err) {
      failed.push({ entry, action: 'grant', message: errorMessage(err, 'Could not add this access.') })
    }
  }

  for (const entry of plan.revokes) {
    if (entry.permissionId === null || entry.permissionId === undefined) {
      failed.push({ entry, action: 'revoke', message: MISSING_ID_NOTE })
      continue
    }
    try {
      await api.revoke(orgId, entry)
      applied.push({ entry, action: 'revoke' })
    } catch (err) {
      failed.push({ entry, action: 'revoke', message: errorMessage(err, 'Could not remove this access.') })
    }
  }

  return { applied, failed }
}

/** The sentence the panel shows once a save finishes. */
export function describeResult(result) {
  const added = result.applied.filter((r) => r.action === 'grant').length
  const removed = result.applied.filter((r) => r.action === 'revoke').length
  const parts = []
  if (added) parts.push(`${added} added`)
  if (removed) parts.push(`${removed} removed`)
  const done = parts.length ? parts.join(', ') : 'Nothing changed'
  if (!result.failed.length) return `Access saved — ${done.toLowerCase()}.`
  return `${done}. ${result.failed.length} change${result.failed.length === 1 ? '' : 's'} did not apply.`
}

// ── who gets the control ────────────────────────────────────────────────────

const ADMIN_ROLES = ['admin', 'owner', 'org_admin', 'superadmin', 'super_admin']

/** Org admins already hold everything, so the row says so instead of offering a panel. */
export function isOrgAdminMember(member) {
  if (!member) return false
  if (member.owner === true || member.is_owner === true) return true
  return ADMIN_ROLES.includes(str(member.role).trim().toLowerCase())
}

export const ADMIN_ROW_NOTE = 'Admins already manage every domain and certificate.'
